import type { Edge } from 'reactflow';
import { edgeFromSourceToTarget } from '__pages/AutomationEngine/Engine/Flow/layout';
import {
  nodeFromTrigger,
  nodeForTransformAdd,
  nodeForAdditionalAdd,
  nodeForUpload,
  edgeFromTriggerToAdditionalVariables,
  edgeFromAdditionalVariablesToAdd,
  isEdge,
} from 'pages/AutomationEngine/Engine/Flow/layout-helpers';
import { NODE_TYPES } from 'pages/AutomationEngine/Engine/Flow/conatants';
import type { FlowNode } from 'pages/AutomationEngine/Engine/Flow/types';
import { FlowStep, FlowTrigger } from '../types';

export const nodeFromStep = (step: FlowStep) => ({
  id: step.id,
  type: NODE_TYPES.automationStep,
  data: step,
  position: { x: -1, y: -1 },
  className: 'nopan',
});

const nodeFromConnectionStep = (step: FlowStep) => ({
  id: step.id,
  type: step.type,
  data: step,
  position: { x: -1, y: -1 },
  className: 'nopan',
});

interface iSmartConnectorToNode {
  steps: FlowStep[];
  triggers: FlowTrigger[];
  connections: FlowStep[];
  uploads: FlowStep[];
}

export const smartConnectorToNodes = ({
  steps,
  triggers,
  connections,
  uploads,
}: iSmartConnectorToNode) => {
  const addAdditionVariables = nodeForAdditionalAdd();
  const addTransformNode = nodeForTransformAdd();
  const triggerNodes = triggers.map(nodeFromTrigger);

  const connectionNodes = connections.length
    ? connections.flatMap((step, index) => {
        return [
          index === 0 && edgeFromSourceToTarget(triggerNodes[0], step),
          nodeFromConnectionStep(step),
          index === 0
            ? edgeFromSourceToTarget(step, addTransformNode)
            : edgeFromSourceToTarget(
                step,
                connections[0],
                'handle-bottom',
                'handle-top'
              ),
        ];
      })
    : [
        edgeFromTriggerToAdditionalVariables(triggers[0]),
        addAdditionVariables,
        edgeFromAdditionalVariablesToAdd(addTransformNode),
      ];

  const firstAddUploadNode = nodeForUpload(0);

  const transfromNodes = [
    addTransformNode,
    edgeFromSourceToTarget(addTransformNode, firstAddUploadNode),
  ];

  const uploadNodes = [
    firstAddUploadNode,
    uploads.length && edgeFromSourceToTarget(firstAddUploadNode, uploads[0]),
    ...uploads.flatMap((step, i) => {
      const addUploadNode = nodeForUpload(i + 1, i === uploads.length - 1);

      return [
        nodeFromStep(step),
        edgeFromSourceToTarget(step, addUploadNode),
        addUploadNode,
        i < uploads.length - 1 &&
          edgeFromSourceToTarget(addUploadNode, uploads[i + 1]),
      ];
    }),
  ];

  return [
    ...connectionNodes,
    ...triggerNodes,
    ...transfromNodes,
    ...uploadNodes,
  ].reduce<[FlowNode[], Edge[]]>(
    (collect, node) => {
      if (node) {
        if (isEdge(node)) {
          collect[1].push(node);
        } else {
          collect[0].push(node);
        }
      }

      return collect;
    },
    [[], []]
  );
};
